import {useEffect, useState, useMemo, useRef, useCallback} from 'react';
import SearchService from '../../services/search.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import { FluxButton, FluxCircularProgress, FluxList, FluxListSubheader, FluxListItem } from '@meltwater/flux-web-components-react';
import { Stack } from '@mui/material';
import csvDownload from 'json-to-csv-export';
import styles from './search.module.css';
import ConfirmationDialog from '../../components/confirmationdialog';
import { useSearchData } from './searchexports';
import DataTable from '../../components/DataTable';

const CustomCat = ({ load, setActiveTab }) => {
  const [catData, setCatData] = useState(null);
  const [validating, setValidating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [severity, setSeverity] = useState('info');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('Loading custom categories...');

  const stopValidationRef = useRef(false);
  const loadingTimerRef = useRef(null);
  const { customCatData: cachedCatData, setCustomCatData: setCachedCatData } = useSearchData();
  const { companyId } = useCompanyId();

  // Custom guide steps specific to CustomCat
  const customCatGuideSteps = useMemo(() => [
    {
      title: 'Custom Categories Management',
      text: 'This guide will help you navigate the Custom Categories Management features.',
      element: null,
    },
    {
      title: 'Search Categories',
      text: 'Use the search box to quickly find custom categories by name or ID.',
      element: '.mrt-input',
      position: { top: 20, left: 200 }
    },
    {
      title: 'Column Visibility & Table Settings',
      text: 'Use these buttons to show or hide specific columns and adjust table settings.',
      element: '.mrt-button.mrt-dropdown-button',
      position: { top: 0, right: 80 }
    },
    {
      title: 'Export No Query',
      text: 'Export all custom categories without their boolean queries.',
      element: '.MuiButtonBase-root.FluxButton-root',
      position: { top: 0, left: 100 }
    },
    {
      title: 'Export Selected',
      text: 'Export selected custom categories with their boolean queries. This requires additional API calls and may take longer.',
      element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(2)',
      position: { top: 0, left: 200 }
    },
    {
      title: 'Export Information',
      text: 'Exporting with boolean queries requires retrieving detailed information for each category, which takes approximately 1.2 seconds per category.',
      element: null,
    },
    {
      title: 'Refresh Data',
      text: 'Use this button to refresh custom category data from the server.',
      element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(3)',
      position: { top: 0, left: 300 }
    },
    {
      title: 'Category Selection',
      text: 'Select categories with checkboxes to perform operations on specific categories.',
      element: '.mrt-row-select-checkbox',
      position: { top: 100, left: 30 }
    },
    {
      title: 'Category Type',
      text: 'The Type column shows whether a category is system-defined or custom-created.',
      element: '.mrt-cell:nth-of-type(3)',
      position: { top: 100, left: 200 }
    }
  ], []);

  const columns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        enableColumnFilterModes: false,
        filterFn: 'contains',
      },
      {
        header: 'id',
        accessorKey: 'id',
      },
      {
        header: 'Type',
        accessorFn: (row) => `${row.type}`,
        filterVariant: 'select',
      },
      {
        header: 'Boolean',
        accessorKey: 'booleanQuery',
      },
    ],
    [],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setLoadingMessage('Fetching custom categories...');
    loadingTimerRef.current = setTimeout(() => {
      setLoadingMessage('Please wait, it takes longer than usual for larger companies...');
    }, 15000);
    
    try {
      const myres = await SearchService.getCustomCats(companyId);
      setCatData(myres.data);
      setCachedCatData(myres.data);
      
      setFeedbackMsg(`Successfully loaded ${myres.data.length} custom categories.`);
      setSeverity('success');
      setOpenSnackBar(true);
      setHasError(false);
    } catch (error) {
      console.log(`FAIL`, error);
      const errorMsg = error.message || 'Unknown error';
      setErrorMessage(errorMsg);
      setFeedbackMsg(`Error loading custom categories: ${errorMsg}`);
      setSeverity('error');
      setOpenSnackBar(true);
      setHasError(true);
    } finally {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setLoadingMessage('');
      setIsLoading(false);
    }
  }, [companyId, loadingTimerRef, setCachedCatData, setFeedbackMsg, setSeverity, setOpenSnackBar, setHasError, setErrorMessage]);
  
  useEffect(() => {
    async function loadData() {
      if (cachedCatData) {
        setCatData(cachedCatData);
      } else if (load) {
        await fetchData();
      }
    }
    
    if (load) {
      loadData();
    }
    
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [load, cachedCatData, companyId, fetchData]);

  const handleCloseSnackBar = () => {
    setOpenSnackBar(false);
  };
  

  const refreshData = async () => {
    try {
      setIsLoading(true);
      setCachedCatData(null);
      await fetchData();
      setFeedbackMsg('Data refreshed successfully');
      setSeverity('success');
    } catch (err) {
      console.error('Error refreshing data:', err);
      setFeedbackMsg(`Error refreshing data: ${err.message}`);
      setSeverity('error');
    } finally {
      setIsLoading(false);
      setOpenSnackBar(true);
    }
  };

  const exportfullrow = async (rows) => {
    const finalexport = [];
    setValidating(true);

    for (const [index, oneitem] of rows.entries()) {
      console.log("validate-stopValidation", stopValidationRef.current);
      if (stopValidationRef.current) break;

      try {
        setProgress((prevProgress) => {
          const newProgress = Math.round((index / rows.length) * 100);
          return newProgress;
        });

        const myres = await SearchService.getCustomCat(companyId, oneitem.original.id);
        console.log("myres", myres);
        const response = {
          name: myres.data.name,
          id: myres.data.id,
          createdAt: myres.data.createdAt,
          createdBy: myres.data.createdBy,
          updatedAt: myres.data.updatedAt,
          updatedBy: myres.data.updatedBy,
          type: myres.data.type,
          booleanQuery: myres.data.booleanQuery,
        };
  
        finalexport.push(response);
      } catch (error) {
        finalexport.push({"status": "fail"});
        console.log(`FAIL`, error);
      }
    }
    
    setValidating(false);
    stopValidationRef.current = false;

    const data = {
      data: finalexport,
      delimiter: ',',
      filename: `Customcategories for ${companyId}`,
    };

    csvDownload(data);
  };

  function getMessage(count) {
    const guesstime = count *1.2;
    let timemessage;
    if (guesstime < 60) {
      timemessage = "under a minute";
    } else {
      const newtime = Math.round(guesstime/60) + 1;
      timemessage = `${newtime} minutes`;
    }
  
    const retmessage = (
      <FluxList>
        <FluxListSubheader>
          {`Download with Boolean Query should take ${timemessage}`}
        </FluxListSubheader>
        <FluxListItem>
          {`Are you sure you want to perform this action?`}
        </FluxListItem>
      </FluxList>
    );
    return retmessage;
  }
  
  const handleExportData = () => {
    const exportdata = catData.map((oneitem) => {
      return {
        name: oneitem.name,
        id: oneitem.id,
        createdAt: oneitem.createdAt,
        createdBy: oneitem.createdBy,
        updatedAt: oneitem.updatedAt,
        updatedBy: oneitem.updatedBy,
        type: oneitem.type,
        booleanQuery: "<not downloaded>",
      }
    });
    
    const data = {
      data: exportdata,
      delimiter: ',',
      filename: `Custom Cats for ${companyId}`,
    }
    
    csvDownload(data);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };
  
  const handleConfirmAction = (rows) => {
    exportfullrow(rows);
    handleCloseDialog();
  };
  
  const handleStopValidation = () => {
    stopValidationRef.current = true;
  };
  
  // Custom toolbar actions for the DataTable
  const renderToolbarActions = ({ table }) => {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <FluxButton
          style={{ margin: 2 }}
          tint="primary"
          type="filled"
          onClick={handleExportData}
        >
          Export All No Query
        </FluxButton>
        <FluxButton
          style={{ margin: 2 }}
          tint="accent"
          type="filled"
          onClick={handleOpenDialog}
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
        >
          Export Selected
        </FluxButton>
        <FluxButton
          style={{ margin: 2 }}
          tint="secondary"
          type="filled"
          onClick={refreshData}
        >
          Refresh Data
        </FluxButton>
        <ConfirmationDialog
          open={openDialog}
          handleClose={handleCloseDialog}
          handleConfirm={handleConfirmAction}
          title="Confirmation"
          message={getMessage(table.getSelectedRowModel().rows.length)}
          confirmParams={table.getSelectedRowModel().rows} 
        />
      </Stack>
    );
  };
  
  return (
    <div className={styles.Search}>
      {validating && (
        <div className={styles.validationProgress}>
          <FluxCircularProgress
            percent={progress}
          >{`Get categories ${progress}% `}</FluxCircularProgress>
          <FluxButton type='outline' tint='accent' onClick={handleStopValidation}>
            Stop
          </FluxButton>
        </div>
      )}
      
      <DataTable 
        columns={columns}
        data={catData || []}
        isLoading={isLoading && !hasError}
        loadingMessage={loadingMessage || 'Loading Custom Categories...'}
        hasError={hasError}
        errorMessage={errorMessage}
        errorTitle="Error Loading Custom Category Data"
        onBackClick={() => setActiveTab(null)}
        renderToolbarActions={renderToolbarActions}
        showGuide={false}
        guideSteps={customCatGuideSteps}
        backTooltip="Return to Dashboard"
        snackbarProps={{
          open: openSnackBar,
          onClose: handleCloseSnackBar,
          message: feedbackMsg,
          severity: severity,
          autoHideDuration: 10000
        }}
        tableProps={{
          muiSearchTextFieldProps: {
            placeholder: 'Search categories...',
            variant: 'standard',
            sx: { width: '300px' }
          },
          initialState: {
            density: 'compact',
            columnVisibility: {
              booleanQuery: false,
            },
            pagination: { pageSize: 100 },
          }
        }}
      />
    </div>
  );
};

export default CustomCat;
